import { useRef, useState } from "react";

type SingleFlightState = { status: "idle" } | { status: "running" };

/**
 * Owns one asynchronous terminal action at a time. The ref closes the
 * same-render event gap synchronously; state drives disabled/loading UI.
 */
function useSingleFlightAction(): {
  state: SingleFlightState;
  run: (action: () => Promise<void>) => Promise<void>;
} {
  const [state, setState] = useState<SingleFlightState>({
    status: "idle",
  });
  const stateRef = useRef<SingleFlightState>({ status: "idle" });

  async function run(action: () => Promise<void>): Promise<void> {
    if (stateRef.current.status === "running") return;
    const runningState: SingleFlightState = { status: "running" };
    stateRef.current = runningState;
    setState(runningState);
    try {
      await action();
    } finally {
      const idleState: SingleFlightState = { status: "idle" };
      stateRef.current = idleState;
      setState(idleState);
    }
  }

  return { state, run };
}

export { useSingleFlightAction };
